import {FirebaseApp, initializeApp} from 'firebase/app';
import {fetchAndActivate, getRemoteConfig, getValue, RemoteConfig} from 'firebase/remote-config';
import {create} from 'zustand';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {BrandType, RegionType} from '@shared/cci/features/const/environment';

export type FeatureFlagVisibility = 'always' | 'hidden' | 'whitelist' | 'whitelist_always' | 'whitelist_server_spec' | 'server_spec';

export type FeatureFlagKey =
  | 'EU_SAFETY_SCORE'
  | 'EU_TIRE_PRESSURE'
  | 'EU_EV_BATTERY'
  | 'EU_UBI'
  | 'KR_SEST_AUTO_SYNC_SUSPENSION_POPUP'
  | 'KR_MOBILITY_CONSUMPTION'
  | 'NA_SAFETY_SCORE'
  | 'NA_TIRE_PRESSURE'
  | 'NA_EV_BATTERY'
  | 'NA_UBI';

interface ResolveFeatureFlagOptions {
  serverSpecVisible?: boolean;
  defaultVisible?: boolean;
}

interface FeatureFlagBrandConfig {
  visibility: FeatureFlagVisibility;
}

type FeatureFlagConfig = Partial<Record<FeatureFlagKey, Partial<Record<BrandType, FeatureFlagBrandConfig>>>>;

export interface FirebaseConfigState {
  isMaintenance: boolean;
  maintenanceMessage: string;

  globalVinWhitelist: Array<string>;
  capitalVinWhitelist: Array<string>;
  naVinWhitelist: Array<string>;
  externalVariables: Record<string, string>;
  featureFlags: FeatureFlagConfig | null;

  init(): Promise<void>;

  checkGlobalVinWhitelist(vin: string): boolean;

  checkCapitalVinWhitelist(vin: string): boolean;

  checkNaVinWhitelist(vin: string): boolean;

  getExternalVariable(key: string): string;

  resolveFeatureFlag(key: FeatureFlagKey, vin: string, options?: ResolveFeatureFlagOptions): boolean | undefined;
}

const DI_MAINTENANCE_VALUE_KEY = 'diMaintenance';
const DI_WHITELIST_VALUE_KEY = 'diWhitelist';
const DI_EXTERNAL_VARIABLES_VALUE_KEY = 'diExternalVariables';
const DI_FEATURE_FLAGS_VALUE_KEY = 'diFeatureFlags';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let remoteConfig: RemoteConfig | null = null;

try {
  app = initializeApp(firebaseConfig);
  remoteConfig = getRemoteConfig(app);
  remoteConfig.settings.minimumFetchIntervalMillis = 1000 * 60 * 5;
} catch {
  app = null;
  remoteConfig = null;
}
type ServiceKey = 'kdia' | 'hdia' | 'gdia' | 'kdiw' | 'hdiw' | 'gdiw';

type MaintenanceConfig = Record<
  RegionType,
  Record<
    ServiceKey,
    {
      isMaintenance: boolean;
      maintenanceMessage: string;
    }
  >
>;

const useFirebaseStore = create<FirebaseConfigState>((set, get) => ({
  isMaintenance: false,
  maintenanceMessage: '',
  globalVinWhitelist: [],
  capitalVinWhitelist: [],
  naVinWhitelist: [],
  externalVariables: {},
  featureFlags: null,

  async init() {
    if (!remoteConfig) {
      set({
        isMaintenance: false,
        maintenanceMessage: '',
        globalVinWhitelist: [],
        capitalVinWhitelist: [],
        naVinWhitelist: [],
        externalVariables: {},
        featureFlags: null,
      });
      return;
    }
    try {
      await fetchAndActivate(remoteConfig);

      const maintenanceValue = getValue(remoteConfig, DI_MAINTENANCE_VALUE_KEY);

      const allMaintenanceConfig = JSON.parse(maintenanceValue.asString()) as MaintenanceConfig;
      const region = environmentStore.getState().region;
      const maintenanceConfig = allMaintenanceConfig[region][import.meta.env.VITE_PLATFORM as ServiceKey];

      const whitelistValue = getValue(remoteConfig, DI_WHITELIST_VALUE_KEY);
      const whitelistConfig = JSON.parse(whitelistValue.asString()) as {
        globalVinWhitelist: Array<string>;
        capitalVinWhitelist: Array<string>;
        naVinWhitelist: Array<string>;
      };

      const externalVariablesValue = getValue(remoteConfig, DI_EXTERNAL_VARIABLES_VALUE_KEY);
      const externalVariables = JSON.parse(externalVariablesValue.asString()) as Record<string, string>;

      let featureFlags: FeatureFlagConfig | null = null;
      try {
        const featureFlagsStr = getValue(remoteConfig, DI_FEATURE_FLAGS_VALUE_KEY).asString();
        if (featureFlagsStr) {
          featureFlags = JSON.parse(featureFlagsStr) as FeatureFlagConfig;
        }
      } catch (e) {
        console.error('firebase featureFlags init 실패:', e);
        // featureFlags 파싱 실패는 무시 (기본값으로 서버스펙 사용)
      }

      set({
        isMaintenance: maintenanceConfig.isMaintenance || false,
        maintenanceMessage: maintenanceConfig.maintenanceMessage || '',
        globalVinWhitelist: whitelistConfig.globalVinWhitelist || [],
        capitalVinWhitelist: whitelistConfig.capitalVinWhitelist || [],
        naVinWhitelist: whitelistConfig.naVinWhitelist || [],
        externalVariables: externalVariables || {},
        featureFlags,
      });
    } catch {
      // firebase 조회실패는 무시
    }
  },
  checkGlobalVinWhitelist(vin: string) {
    return get().globalVinWhitelist.includes(vin);
  },
  checkCapitalVinWhitelist(vin: string) {
    return get().capitalVinWhitelist.includes(vin);
  },
  checkNaVinWhitelist(vin: string) {
    return get().naVinWhitelist.includes(vin);
  },
  resolveFeatureFlag(key: FeatureFlagKey, vin: string, options?: ResolveFeatureFlagOptions) {
    const brand = import.meta.env.VITE_BRAND as BrandType;
    const visibility = get().featureFlags?.[key]?.[brand]?.visibility;
    const isWhitelisted = get().globalVinWhitelist.includes(vin);

    switch (visibility) {
      case 'always':
        return true;
      case 'hidden':
        return false;
      case 'whitelist':
      case 'whitelist_always':
        return isWhitelisted;
      case 'whitelist_server_spec':
        return isWhitelisted && !!options?.serverSpecVisible;
      case 'server_spec':
        return options?.serverSpecVisible;
      default:
        return options?.defaultVisible ?? false;
    }
  },
  getExternalVariable(key: string) {
    switch (key) {
      case 'SCC_URL':
        return get().externalVariables.SCC_URL || 'https://youtu.be/q-WwBhEwrF0?si=A3P6W-vHAkvl3dob';
      case 'ECOMILEAGE_INTRO_URL':
        return get().externalVariables.ECOMILEAGE_INTRO_URL || 'https://ecomileage.seoul.go.kr/goods/apply.do';
      case 'LICENSE_RENEWAL_URL':
        return get().externalVariables.LICENSE_RENEWAL_URL || 'https://www.safedriving.or.kr';
      case 'SAFE_DRIVING_URL':
        return get().externalVariables.SAFE_DRIVING_URL || 'https://www.safedriving.or.kr';
      case 'EMERGENCY_TEL_LOTTE':
        return get().externalVariables.EMERGENCY_TEL_LOTTE || '1588-3344';
      case 'EMERGENCY_TEL_MERITZ':
        return get().externalVariables.EMERGENCY_TEL_MERITZ || '1566-7711';
      case 'EMERGENCY_TEL_SAMSUNG':
        return get().externalVariables.EMERGENCY_TEL_SAMSUNG || '1588-5114';
      case 'EMERGENCY_TEL_CARROT':
        return get().externalVariables.EMERGENCY_TEL_CARROT || '1566-0300';
      case 'EMERGENCY_TEL_HANA':
        return get().externalVariables.EMERGENCY_TEL_HANA || '1566-3000';
      case 'EMERGENCY_TEL_HANHWA':
        return get().externalVariables.EMERGENCY_TEL_HANHWA || '1566-8000';
      case 'EMERGENCY_TEL_HEUNGKUK':
        return get().externalVariables.EMERGENCY_TEL_HEUNGKUK || '1688-1688';
      case 'EMERGENCY_TEL_HYUNDAI':
        return get().externalVariables.EMERGENCY_TEL_HYUNDAI || '1588-5656';
      case 'EMERGENCY_TEL_AXA':
        return get().externalVariables.EMERGENCY_TEL_AXA || '1566-2266';
      case 'EMERGENCY_TEL_DB':
        return get().externalVariables.EMERGENCY_TEL_DB || '1588-0100';
      case 'EMERGENCY_TEL_KB':
        return get().externalVariables.EMERGENCY_TEL_KB || '1544-0114';
      case 'KR_SEST_AUTO_SYNC_SUSPENSION_START_DATE':
        return get().externalVariables.KR_SEST_AUTO_SYNC_SUSPENSION_START_DATE || '2026-06-24';
      case 'KR_SEST_AUTO_SYNC_SUSPENSION_TEL':
        return get().externalVariables.KR_SEST_AUTO_SYNC_SUSPENSION_TEL || '080-200-2000';
      case 'CAPITAL_EVENT_START_DATE':
        return get().externalVariables.CAPITAL_EVENT_START_DATE;
      case 'CAPITAL_EVENT_END_DATE':
        return get().externalVariables.CAPITAL_EVENT_END_DATE;
      case 'CAPITAL_EVENT_AD_NOTICE':
        return (
          get().externalVariables.CAPITAL_EVENT_AD_NOTICE ||
          '광고심의필\n광고 제작월 : 2025.10\n현대캐피탈 준법감시심의필 251030-002호(2025.10.30~2026.10.29)'
        );
      case 'CAPITAL_EVENT_URL':
        return (
          get().externalVariables.CAPITAL_EVENT_URL ||
          'https://mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-third?hmcType={hmcType}&cbno={cbno}'
        );
      case 'CAPITAL_MYASSETS_URL_BETA':
        return (
          get().externalVariables.CAPITAL_MYASSETS_URL_BETA ||
          'https://preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/myassets'
        );
      case 'CAPITAL_EVENT_URL_BETA':
        return (
          get().externalVariables.CAPITAL_EVENT_URL_BETA ||
          'https://preprod-mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-third?hmcType={hmcType}&cbno={cbno}'
        );
      default:
        return '';
    }
  },
}));

export default useFirebaseStore;
